import * as Popover from "@radix-ui/react-popover";
import { AppWindow, ChevronRight, Clock3, GitBranch, ListOrdered, Repeat2 } from "lucide-react";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { type CaseNoteContext, useCaseNoteText } from "./ProcessCaseNoteBadges";
import ProcessFindingsCarousel from "./ProcessFindingsCarousel";
import type { CaseNote, CaseReading } from "./processCaseNotes";
import { type ProcessInstance, type StepNamesById, stepWaitSeconds } from "./processGraphModel";

const FINDING_ICON = {
  wait: Clock3,
  rework: Repeat2,
  variation: GitBranch,
  app: AppWindow,
  steps: ListOrdered,
};

// Evidence points to occurrences, not just node IDs: a node may appear several times in a case.
const caseFindingStepIndexes = (note: CaseNote, instance: ProcessInstance): number[] => {
  const steps = instance.steps;
  switch (note.kind) {
    case "wait": {
      const waits = stepWaitSeconds(steps);
      const before = waits.findIndex(
        (seconds, index) =>
          seconds === note.seconds && steps[index + 1]?.nodeId === note.beforeStepId,
      );
      return before < 0 ? [] : [before + 1];
    }
    case "rework": {
      const seen = new Set<string>();
      return steps.flatMap((step, index) => {
        const returned = seen.has(step.nodeId) && steps[index - 1]?.nodeId !== step.nodeId;
        seen.add(step.nodeId);
        return returned ? [index] : [];
      });
    }
    case "variation":
      return steps.flatMap((step, index) => (note.stepIds.includes(step.nodeId) ? [index] : []));
    case "app":
      return steps.flatMap((step, index) =>
        (step.appSeconds?.[note.app] ?? 0) > 0 ? [index] : [],
      );
    case "steps":
      return [];
  }
};

export default function ProcessCaseFindings({
  instance,
  reading,
  context,
  nameById,
  onSelectStep,
}: {
  instance: ProcessInstance;
  reading: CaseReading;
  context: CaseNoteContext;
  nameById: StepNamesById;
  onSelectStep: (index: number) => void;
}) {
  const { t } = useTranslation();
  const textFor = useCaseNoteText(nameById, context);
  const supportingStepsRef = useRef<HTMLButtonElement>(null);
  const notes = [...reading.notes].sort(
    (a, b) => Number(b.kind === "wait") - Number(a.kind === "wait"),
  );
  const stepLinkClass =
    "inline-flex min-h-7 items-center gap-1 rounded text-xs font-medium text-info hover:underline focus-visible:ring-2 focus-visible:ring-info/30";

  return (
    <ProcessFindingsCarousel
      findings={notes.map(note => {
        const text = textFor(note);
        const indexes = caseFindingStepIndexes(note, instance);
        return {
          id: note.kind,
          tone: note.tone,
          icon: FINDING_ICON[note.kind],
          title: text.pill,
          body: text.body,
          context: text.context,
          action: (
            <>
              {indexes.length === 1 && (
                <button
                  type="button"
                  onClick={() => onSelectStep(indexes[0])}
                  className={stepLinkClass}
                >
                  {t("processPanel.caseViewStep", { number: indexes[0] + 1 })}
                  <ChevronRight className="h-3 w-3" aria-hidden="true" />
                </button>
              )}
              {indexes.length > 1 && (
                <Popover.Root key={note.kind}>
                  <Popover.Trigger asChild>
                    <button ref={supportingStepsRef} type="button" className={stepLinkClass}>
                      {t("processPanel.caseFindingSteps", { count: indexes.length })}
                      <ChevronRight className="h-3 w-3" aria-hidden="true" />
                    </button>
                  </Popover.Trigger>
                  <Popover.Portal>
                    <Popover.Content
                      aria-label={t("processPanel.caseFindingSteps", { count: indexes.length })}
                      side="bottom"
                      align="start"
                      sideOffset={6}
                      collisionPadding={16}
                      onCloseAutoFocus={event => {
                        // Keep keyboard focus without scrolling back from the chosen timeline step.
                        event.preventDefault();
                        supportingStepsRef.current?.focus({ preventScroll: true });
                      }}
                      className="pd-theme z-50 grid max-h-[min(20rem,var(--radix-popover-content-available-height))] w-96 max-w-[calc(100vw-32px)] gap-1 overflow-y-auto rounded-lg border border-outline bg-surface p-2 shadow-lift"
                    >
                      {indexes.map(stepIndex => (
                        <Popover.Close key={stepIndex} asChild>
                          <button
                            type="button"
                            onClick={() => onSelectStep(stepIndex)}
                            className="flex min-h-8 items-center gap-2 rounded px-2 text-left text-xs text-info hover:bg-surface-strong focus-visible:ring-2 focus-visible:ring-info/30"
                          >
                            <span className="shrink-0 font-medium">
                              {t("processPanel.caseViewStep", { number: stepIndex + 1 })}
                            </span>
                            <span className="min-w-0 flex-1 break-words text-text-mid">
                              {nameById.get(instance.steps[stepIndex].nodeId) ??
                                instance.steps[stepIndex].nodeId}
                            </span>
                            <ChevronRight className="h-3 w-3 shrink-0" aria-hidden="true" />
                          </button>
                        </Popover.Close>
                      ))}
                    </Popover.Content>
                  </Popover.Portal>
                </Popover.Root>
              )}
            </>
          ),
        };
      })}
    />
  );
}
